import React, { useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Category as CategoryIcon
} from '@mui/icons-material';
import { Category } from '../FormBuilder';

interface CategoriesStepProps {
  categories: Category[];
  onChange: (categories: Category[]) => void;
}

const DEFAULT_COLORS = [
  '#2196F3', // Blue
  '#4CAF50', // Green
  '#FF9800', // Orange
  '#F44336', // Red
  '#9C27B0', // Purple
  '#00BCD4', // Cyan
  '#FFEB3B', // Yellow
  '#795548'  // Brown
];

const CategoriesStep: React.FC<CategoriesStepProps> = ({ categories, onChange }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [formValues, setFormValues] = useState({
    nombre: '',
    descripcion: '',
    color: DEFAULT_COLORS[0],
    icono: ''
  });

  const handleOpenDialog = (category?: Category) => {
    if (category) {
      setEditingCategory(category);
      setFormValues({
        nombre: category.nombre,
        descripcion: category.descripcion || '',
        color: category.color || DEFAULT_COLORS[0],
        icono: category.icono || ''
      });
    } else {
      setEditingCategory(null);
      setFormValues({
        nombre: '',
        descripcion: '',
        color: DEFAULT_COLORS[categories.length % DEFAULT_COLORS.length],
        icono: ''
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingCategory(null);
  };

  const handleSaveCategory = () => {
    if (!formValues.nombre.trim()) {
      return;
    }

    if (editingCategory) {
      // Editar categoría existente
      const updatedCategories = categories.map(cat =>
        cat.id === editingCategory.id
          ? { ...cat, ...formValues }
          : cat
      );
      onChange(updatedCategories);
    } else {
      // Agregar nueva categoría (id temporal será asignado por el backend)
      const newCategory: Category = {
        ...formValues,
        id: Date.now() // Temporary ID
      };
      onChange([...categories, newCategory]);
    }

    handleCloseDialog();
  };

  const handleDeleteCategory = (categoryId: number | undefined) => {
    if (!categoryId) return;
    const updatedCategories = categories.filter(cat => cat.id !== categoryId);
    onChange(updatedCategories);
  };

  // Sort categories alphabetically
  const sortedCategories = [...categories].sort((a, b) => 
    a.nombre.localeCompare(b.nombre, 'es', { sensitivity: 'base' })
  );

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h6">
            Categorías del Formulario
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Las categorías son conceptos para análisis y reportes. Cada pregunta puede pertenecer a una categoría independientemente de su sección.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Agregar Categoría
        </Button>
      </Box>

      {categories.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CategoryIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="textSecondary" gutterBottom>
            No hay categorías
          </Typography>
          <Typography variant="body2" color="textSecondary" mb={2}>
            Comience agregando una categoría para organizar las preguntas del formulario
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Agregar Primera Categoría
          </Button>
        </Paper>
      ) : (
        <List>
          {sortedCategories.map((category) => (
            <Paper key={category.id || category.nombre} sx={{ mb: 2 }}>
              <ListItem>
                <Box
                  sx={{
                    width: 8,
                    height: 40,
                    bgcolor: category.color || DEFAULT_COLORS[0],
                    borderRadius: 1,
                    mr: 2
                  }}
                />
                
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="subtitle1">
                        {category.nombre}
                      </Typography>
                      <Chip
                        label="Análisis"
                        size="small"
                        color="default"
                        variant="outlined"
                      />
                    </Box>
                  }
                  secondary={category.descripcion || 'Sin descripción'}
                />
                
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    onClick={() => handleOpenDialog(category)}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    edge="end"
                    onClick={() => handleDeleteCategory(category.id)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            </Paper>
          ))}
        </List>
      )}

      {/* Dialog para crear/editar categoría */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingCategory ? 'Editar Categoría' : 'Nueva Categoría'}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={2}>
            <TextField
              label="Nombre"
              value={formValues.nombre}
              onChange={(e) => setFormValues({ ...formValues, nombre: e.target.value })}
              required
              fullWidth
              autoFocus
            />

            <TextField
              label="Descripción"
              value={formValues.descripcion}
              onChange={(e) => setFormValues({ ...formValues, descripcion: e.target.value })}
              multiline
              rows={3}
              fullWidth
            />

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Color
              </Typography>
              <Box display="flex" gap={1} flexWrap="wrap">
                {DEFAULT_COLORS.map((color) => (
                  <Box
                    key={color}
                    sx={{
                      width: 40,
                      height: 40,
                      bgcolor: color,
                      borderRadius: 1,
                      cursor: 'pointer',
                      border: formValues.color === color ? '3px solid black' : '1px solid #ddd',
                      '&:hover': {
                        opacity: 0.8
                      }
                    }}
                    onClick={() => setFormValues({ ...formValues, color })}
                  />
                ))}
              </Box>
            </Box>

            <TextField
              label="Ícono (opcional)"
              value={formValues.icono}
              onChange={(e) => setFormValues({ ...formValues, icono: e.target.value })}
              fullWidth
              helperText="Nombre del ícono de Material-UI (ej: Security, Build, Assessment)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            Cancelar
          </Button>
          <Button
            onClick={handleSaveCategory}
            variant="contained"
            disabled={!formValues.nombre.trim()}
          >
            {editingCategory ? 'Guardar' : 'Agregar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CategoriesStep;
